import './Facets.scss';
import React, {useCallback, useRef} from 'react';
import {FacetsHeader} from './FacetsHeader/FacetsHeader';
import {FacetsContent} from './FacetsContent/FacetsContent';
import {HibuButton} from '../common/hibuButton/hibuButton';
import {Icons} from '../icons/icons';
import {SvgIcon} from '../icons/svgIcon';
import {useDispatch} from 'react-redux';
import {useIsFiltersPending, useIsShowFilters} from './duck/filter.hooks';
import {changeVisibility} from './duck/filter.actions';
import {Spinner} from '../common/spinner/spinner';
import classNames from 'clsx';
import {DrawerProps, HibuDrawer} from '../common/drawer/hibuDrawer';
import {useTranslation} from 'react-i18next';

type FacetsProps = {
    showSidebar: boolean;
    closeDrawer: () => void;
};

export const Facets = React.memo(({showSidebar, closeDrawer}: FacetsProps) => {
    const dispatch = useDispatch();
    const {t} = useTranslation();

    const showFilters: boolean = useIsShowFilters();
    const filtersPending: boolean = useIsFiltersPending();

    const expandButtonRef = useRef<HTMLButtonElement>(null);
    const collapseButtonRef = useRef<HTMLButtonElement>(null);

    const onExpand = useCallback(() => {
        dispatch(changeVisibility(!showFilters));

        const currentlyFocused = document.activeElement;
        requestAnimationFrame(() => {
            if (currentlyFocused === expandButtonRef.current) {
                collapseButtonRef.current?.focus();
            } else if (currentlyFocused === collapseButtonRef.current) {
                expandButtonRef.current?.focus();
            }
        });
    }, [dispatch, showFilters]);

    const headerMobile = ({onCloseRequest}: DrawerProps) => (
        <SvgIcon
            className="details-header-close"
            icon={Icons.X}
            onClick={onCloseRequest}
            alt={t('general.close')}
        />
    );

    const content = useCallback(() => {
        return (
            <div id="facets" className={filtersPending ? 'fetch-pending' : ''}>
                <FiltersPendingSpinner filtersPending={filtersPending}/>
                <FiltersCollapsed showFilters={showFilters} onExpand={onExpand} ref={collapseButtonRef}/>
                <FiltersExpanded showFilters={showFilters} onExpand={onExpand} ref={expandButtonRef}/>
            </div>
        );
    }, [showFilters, filtersPending, onExpand]);

    return (
        <HibuDrawer
            id={'hibu_facet_column'}
            className={classNames(showFilters ? '' : 'hideContent')}
            show={showSidebar}
            onCloseRequest={closeDrawer}
            headerCbMobile={headerMobile}
            contentCb={content}
        />
    );
});

type FiltersPendingProps = {
    filtersPending: boolean;
};

const FiltersPendingSpinner = ({filtersPending}: FiltersPendingProps) => {
    if (!filtersPending) return null;
    return (
        <Spinner/>
    );
};

type ShowFiltersProps = {
    showFilters: boolean;
    onExpand: () => void;
    ref?: React.Ref<any>;
};

const FiltersCollapsed = ({showFilters, onExpand, ref}: ShowFiltersProps) => {
    return (
        <button id="filters-collapsed" className={classNames(!showFilters ? '' : 'hideContent')} onClick={onExpand}
                ref={ref}>
            <SvgIcon
                icon={Icons.FILTER}
                className={'facet-list-collapsed-filter'}
            />
            <SvgIcon
                icon={Icons.ARROWS}
                className={'facet-list-collapsed-arrows'}
            />
        </button>
    );
};

const FiltersExpanded = ({showFilters, onExpand, ref}: ShowFiltersProps) => {
    return (
        <div id="facets-inner" className={classNames(showFilters ? '' : 'hideContent')}>
            <FacetsHeader/>
            <FacetsContent/>
            <HideFiltersButton onExpand={onExpand} ref={ref}/>
        </div>
    );
};

type HideFiltersProps = {
    onExpand: () => void;
    ref?: React.Ref<any>;
};

const HideFiltersButton = ({onExpand, ref}: HideFiltersProps) => (
    <HibuButton
        ref={ref}
        size={'sm'}
        onClick={onExpand}
        icon={Icons.ARROWS}
        tooltipKey={'search.details.hideDetails'}
        visible={true}
        className={'facet-list-close'}
        placement={'right'}
    />
);
